import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Autocomplete, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@mui/material';
import moment from 'moment';
import type AdminCompaniesStore from '../../store/adminCompanies/adminCompaniesStore';
import type { IAdminUser } from '../../store/adminCompanies/adminCompaniesStore';
import type { TAdminCompanyMember } from '../../services/models/BillingModels';
import useConfirm from '../../hooks/useConfirm';

/** How a member is named on the page: the name Auth0 has, or the owner id when the profiles could not be read. */
const memberName = (member: TAdminCompanyMember) => member.name ?? `Owner ${member.ownerId}`;

/** How a user reads in the Add member field's list. */
const userLabel = (user: IAdminUser) => `${user.name} · ${user.email}`;

/**
 * The Members section of the company page: who has an open membership to the company, by name, email
 * and since when; an Add member field searching the Users tab's rows by name or email; and Remove on
 * each row, confirmed first. A user who is a member of another company is moved only after the
 * backend's own question is answered yes. A thin observer over the admin companies store; refusals
 * show in the page's alert.
 */
const AdminCompanyMembers = observer(function AdminCompanyMembers({ adminCompaniesStore }: { adminCompaniesStore: AdminCompaniesStore }) {
  const { company, memberCandidates, memberSearch, pendingMove, isWorking, isLoadingUsers, usersError } = adminCompaniesStore;
  const [removing, setRemoving] = useState<TAdminCompanyMember | null>(null);
  const [RemoveConfirmation, confirmRemove] = useConfirm(
    `Remove ${removing === null ? '' : memberName(removing)}?`,
    `${removing === null ? '' : memberName(removing)} will no longer be a member of ${company?.name ?? ''}.`,
    true,
    'Remove',
  );

  useEffect(() => {
    void adminCompaniesStore.loadUsers();
  }, [adminCompaniesStore]);

  if (company === null) return null;
  const { members, profilesUnavailable } = company;

  const remove = async (member: TAdminCompanyMember) => {
    setRemoving(member);
    if (await confirmRemove()) await adminCompaniesStore.removeMember(member.ownerId);
  };

  return (
    <Stack spacing={1} data-testid='company-members'>
      <Typography variant='h6'>Members</Typography>
      {profilesUnavailable === true && (
        <Typography variant='body2' color='text.secondary'>
          Names and emails could not be read from Auth0 just now; the members are shown by owner id.
        </Typography>
      )}

      <Autocomplete<IAdminUser>
        options={memberCandidates}
        // The store has already searched; the list is shown as it is.
        filterOptions={(options) => options}
        getOptionLabel={userLabel}
        isOptionEqualToValue={(option, value) => option.user_id === value.user_id}
        value={null}
        inputValue={memberSearch}
        onInputChange={(_event, value, reason) => {
          if (reason === 'input') adminCompaniesStore.setMemberSearch(value);
        }}
        onChange={(_event, user) => {
          if (user !== null) void adminCompaniesStore.addMember(user);
        }}
        disabled={isWorking}
        loading={isLoadingUsers}
        noOptionsText={memberSearch.trim() === '' ? 'Type a name or an email.' : 'No user matches.'}
        renderInput={(params) => <TextField {...params} label='Add member' size='small' helperText={usersError ?? 'Search the users by name or email.'} error={usersError !== null} />}
        sx={{ maxWidth: 480 }}
      />

      {members.length === 0 ? (
        <Typography variant='body2' color='text.secondary'>
          No members.
        </Typography>
      ) : (
        <Table size='small' aria-label='Members'>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Member since</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {members.map((member) => (
              <TableRow key={member.membershipId} data-owner-id={member.ownerId}>
                <TableCell>{memberName(member)}</TableCell>
                <TableCell>{member.email ?? ''}</TableCell>
                <TableCell>{moment(member.memberSinceUtc).format('DD.MM.YYYY')}</TableCell>
                <TableCell align='right'>
                  <Button variant='text' color='error' size='small' onClick={() => void remove(member)} disabled={isWorking}>
                    Remove
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={pendingMove !== null} onClose={adminCompaniesStore.cancelMove} fullWidth>
        <DialogTitle>Move member?</DialogTitle>
        <DialogContent>
          <DialogContentText>{pendingMove?.message}</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant='newSecondary' onClick={() => void adminCompaniesStore.confirmMove()}>
            Move
          </Button>
          <Button variant='newSecondary' onClick={adminCompaniesStore.cancelMove}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
      <RemoveConfirmation />
    </Stack>
  );
});

export default AdminCompanyMembers;
